import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabase';
import { S, T, AUTH_STARS, makeReferralCode, decodeReferralCode, getReferralFromUrl, fireConfetti, playSound } from '../utils/appUtils';
import { FoundingCounter } from '../components/Scarcity/FoundingBadge';

// ── PREMIUM AUTH SCREEN ──────────────────────────────────────
export default function AuthScreen({onAuth,onTeacherPortal,skipSplash}){
  const [mode,setMode]=useState(skipSplash?'desktop':'landing'); // landing|login|signup|desktop
  const [email,setEmail]=useState('');
  const [password,setPassword]=useState('');
  const [name,setName]=useState('');
  const [cls,setCls]=useState(null);
  const [step,setStep]=useState(1);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const [showPwd,setShowPwd]=useState(false);
  const [refCode]=useState(()=>getReferralFromUrl());
  const [foundingCount,setFoundingCount]=useState(null);

  useEffect(()=>{
    supabase.from('neevstar_founding_counter').select('count').eq('id',1).single()
      .then(({data})=>{ if(data) setFoundingCount(data.count); }).catch(()=>{});
  },[]);

  const handleGoogleLogin=async()=>{
    setLoading(true);setError('');
    try{
      const{error:e}=await supabase.auth.signInWithOAuth({
        provider:'google',
        options:{
          redirectTo:'https://neevstar.com',
          queryParams:{prompt:'select_account'}
        }
      });
      if(e){
        setError('Google Sign In failed. Please try email login instead.');
        setLoading(false);
      }
    }catch(err){
      setError('Google Sign In failed. Please try email login instead.');
      setLoading(false);
    }
  };

  const handleLogin=async()=>{
    if(!email.trim()){setError('Please enter your email address');return;}
    if(!password||password.length<1){setError('Please enter your password');return;}
    setLoading(true);setError('');
    const loginSafetyTimer=setTimeout(()=>{setLoading(false);setError('Taking too long. Please check your connection and try again.');},12000);
    try{
      const{data,error:e}=await supabase.auth.signInWithPassword({
        email:email.trim().toLowerCase(),password
      });
      if(e){
        clearTimeout(loginSafetyTimer);
        console.error('Login error:',e);
        if(e.message.includes('Invalid login')||e.message.includes('invalid')){
          const{data:prof}=await supabase.from('bm_child_profiles')
            .select('parent_email').eq('parent_email',email.trim().toLowerCase()).single();
          if(prof){
            setError('This account uses Google Sign In. Please use the Continue with Google button.');
            setLoading(false);return;
          }
          setError('Wrong email or password. Please try again.');
        } else if(e.message.includes('not confirmed')){
          setError('Please check your email and click the confirmation link first.');
        } else {
          setError(e.message||'Login failed. Please try again.');
        }
        setLoading(false);return;
      }
      const{data:profile}=await supabase.from('bm_child_profiles').select('*').eq('parent_email',email.trim().toLowerCase()).single();
      if(profile){
        const updates={};
        if(!profile.city){const detectedCity=await detectCity();if(detectedCity)updates.city=detectedCity;}
        if(!profile.auth_user_id)updates.auth_user_id=data.user.id;
        if(Object.keys(updates).length>0) await supabase.from('bm_child_profiles').update(updates).eq('parent_email',email.trim().toLowerCase());
      }
      clearTimeout(loginSafetyTimer);
      onAuth(data.user,profile);
    }catch(err){
      clearTimeout(loginSafetyTimer);
      console.error('Login exception:',err);
      setError('Something went wrong. Please check your connection and try again.');
      setLoading(false);
    }
  };

  const detectCity=async()=>{
    try{
      const controller=new AbortController();
      const timeout=setTimeout(()=>controller.abort(),3000); // 3s max
      const r=await fetch('https://ipapi.co/json/',{signal:controller.signal});
      clearTimeout(timeout);
      const d=await r.json();
      return d.city||d.region||null;
    }catch{return null;}
  };

  const handleSignup=async()=>{
    if(!cls){setError('Please select your class');return;}
    if(!name.trim()){setError('Please enter your name');return;}
    if(!email.trim()){setError('Please enter your email');return;}
    if(password.length<6){setError('Password must be at least 6 characters');return;}
    setLoading(true);setError('');
    // Safety net — never stay loading more than 15 seconds
    const safetyTimer=setTimeout(()=>{setLoading(false);setError('Taking too long. Please try again.');},15000);

    // Check if email already exists BEFORE signup attempt
    const{data:existingCheck}=await supabase.from('bm_child_profiles')
      .select('parent_email').eq('parent_email',email.trim().toLowerCase()).single();
    if(existingCheck){
      clearTimeout(safetyTimer);
      setError('This email already has an account. Please tap Log In instead.');
      setLoading(false);
      setTimeout(()=>setMode('login'),2000);
      return;
    }

    const{data,error:e}=await supabase.auth.signUp({
      email:email.trim().toLowerCase(),
      password
    });
    
    // Handle existing user errors
    if(e){
      clearTimeout(safetyTimer);
      if(e.message.toLowerCase().includes('already')||
         e.message.toLowerCase().includes('registered')||
         e.message.toLowerCase().includes('exists')){
        setError('This email already has an account. Please tap Log In instead.');
      } else if(e.message.toLowerCase().includes('invalid')||
                e.message.toLowerCase().includes('format')){
        setError('Please enter a valid email address (e.g. name@gmail.com)');
      } else {
        setError(e.message);
      }
      setLoading(false);
      return;
    }

    // Supabase sometimes returns success for existing email with empty identities
    if(!data?.user?.identities||data.user.identities.length===0){
      clearTimeout(safetyTimer);
      setError('This email already has an account. Please tap Log In instead.');
      setLoading(false);
      setTimeout(()=>setMode('login'),2000);
      return;
    }

    // Create profile
    try{
      const detectedCity=await detectCity();
      const referralBonus=refCode?100:0;
      const neevUsername='NeevExplorer_'+Math.floor(1000+Math.random()*9000);
      const{data:profile,error:pe}=await supabase.from('bm_child_profiles')
        .insert({parent_email:email,child_name:name,class_level:cls,xp_points:referralBonus,streak_count:0,current_level:1,city:detectedCity,neev_username:neevUsername,auth_user_id:data.user.id})
        .select().single();
      if(pe){console.error('Profile create error:',pe);}

      // Founding Explorer — check and assign if under 100
      if(profile){
        try{
          const{data:counter}=await supabase.from('neevstar_founding_counter').select('count').eq('id',1).single();
          const currentCount=counter?.count||0;
          if(currentCount<100){
            const newCount=currentCount+1;
            await supabase.from('neevstar_founding_counter').update({count:newCount}).eq('id',1);
            await supabase.from('bm_child_profiles')
              .update({is_founding_explorer:true,founding_explorer_number:newCount})
              .eq('parent_email',email.trim().toLowerCase());
          }
        }catch(_){}
      }

      // Award XP to referrer + Lucky notification
      if(refCode){
        const referrerEmail=decodeReferralCode(refCode);
        if(referrerEmail&&referrerEmail!==email.trim().toLowerCase()){
          const{data:referrer}=await supabase.from('bm_child_profiles')
            .select('xp_points,child_name,pet_name,pet_type').eq('parent_email',referrerEmail).single();
          if(referrer){
            await supabase.from('bm_child_profiles')
              .update({xp_points:(referrer.xp_points||0)+100})
              .eq('parent_email',referrerEmail);
            fetch('/api/send-referral-push',{
              method:'POST',
              headers:{'Content-Type':'application/json'},
              body:JSON.stringify({
                referrerEmail,
                referrerName:referrer.child_name,
                petName:referrer.pet_name||'Lucky',
                petType:referrer.pet_type||'lumie',
                newFriendName:name||'Someone',
              }),
            }).catch(()=>{});
          }
        }
      }

      clearTimeout(safetyTimer);
      playSound('levelup');fireConfetti();
      onAuth(data.user,profile);
    }catch(err){
      clearTimeout(safetyTimer);
      console.error('Signup profile exception:',err);
      setError('Account created! Please log in.');
      setLoading(false);
      setTimeout(()=>setMode('login'),2000);
    }
  };

  // ── LANDING SCREEN ──────────────────────────────────────────
  if(mode==='desktop'){
    return(
      <div style={{padding:'40px 36px',fontFamily:"'Nunito',sans-serif"}}>
        <div style={{marginBottom:32,textAlign:'center'}}>
          <p style={{fontSize:13,fontWeight:900,color:'#888',letterSpacing:'0.06em',margin:'0 0 6px'}}>WELCOME BACK</p>
          <h2 style={{fontSize:24,fontWeight:900,color:'#1A1A2E',margin:0}}>Sign in to NeevStar</h2>
        </div>
        {error&&<div style={{background:'#FFF0F0',borderRadius:12,padding:'10px 14px',marginBottom:16,
          borderLeft:'4px solid #FF2D55'}}>
          <p style={{fontSize:13,fontWeight:800,color:'#FF2D55',margin:0}}>⚠️ {error}</p>
        </div>}
        <motion.button whileTap={{scale:0.97}} whileHover={{scale:1.01}}
          style={{width:'100%',padding:'14px',borderRadius:'14px',border:'2px solid #E8E8E8',
            background:'white',display:'flex',alignItems:'center',justifyContent:'center',
            gap:'10px',cursor:'pointer',marginBottom:'16px',
            fontFamily:"'Nunito',sans-serif",fontSize:'15px',fontWeight:'800',color:'#333'}}
          onClick={handleGoogleLogin}>
          <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continue with Google
        </motion.button>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
          <div style={{flex:1,height:1,background:'#E8E8E8'}}/>
          <p style={{margin:0,fontSize:12,fontWeight:700,color:'#AAA'}}>OR</p>
          <div style={{flex:1,height:1,background:'#E8E8E8'}}/>
        </div>
        <div style={{display:'flex',gap:10,marginBottom:16}}>
          <motion.button whileTap={{scale:0.97}}
            style={{flex:1,padding:'13px',borderRadius:'13px',border:'2px solid #E8E8E8',
              background:'white',cursor:'pointer',fontFamily:"'Nunito',sans-serif",
              fontSize:'15px',fontWeight:'800',color:'#333'}}
            onClick={()=>{setMode('login');setError('');}}>
            Log In
          </motion.button>
          <motion.button whileTap={{scale:0.97}}
            style={{flex:1,padding:'13px',borderRadius:'13px',border:'none',
              background:'linear-gradient(135deg,#FFD600,#FF6B00)',cursor:'pointer',
              fontFamily:"'Nunito',sans-serif",fontSize:'15px',fontWeight:'900',color:'white',
              boxShadow:'0 4px 14px rgba(255,107,0,0.35)'}}
            onClick={()=>{setMode('signup');setError('');setStep(1);}}>
            Sign Up Free 🚀
          </motion.button>
        </div>
        {onTeacherPortal&&(
          <button onClick={onTeacherPortal}
            style={{width:'100%',padding:'11px',borderRadius:'13px',border:'2px solid #E8E8E8',
              background:'#F8F9FF',cursor:'pointer',fontFamily:"'Nunito',sans-serif",
              fontWeight:800,fontSize:13,color:'#555',display:'flex',alignItems:'center',
              justifyContent:'center',gap:8}}>
            🏫 Teacher / School Login
          </button>
        )}
        <p style={{textAlign:'center',fontSize:11,color:'#CCC',fontWeight:700,margin:'16px 0 0'}}>
          Free to start • Classes 3–8 • Works on any phone
        </p>
      </div>
    );
  }

  if(mode==='landing'){
    return(
      <div style={{minHeight:'100vh',background:'linear-gradient(160deg,#0A0E27 0%,#1A0533 50%,#0D1B4B 100%)',
        display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'space-between',
        padding:'0',overflow:'hidden',position:'relative',fontFamily:"'Nunito',sans-serif"}}
        className="ns-auth-screen">

        {/* Animated stars background — values pre-computed so they're stable across re-renders */}
        <div style={{position:'absolute',top:0,left:0,right:0,bottom:0,overflow:'hidden',pointerEvents:'none'}}>
          {AUTH_STARS.map((s,i)=>(
            <motion.div key={i}
              style={{position:'absolute',left:s.left,top:s.top,fontSize:s.size,opacity:0.6}}
              animate={{y:[0,-20,0],opacity:[0.3,0.8,0.3],scale:[1,1.3,1]}}
              transition={{duration:s.duration,repeat:Infinity,delay:s.delay}}>
              {s.icon}
            </motion.div>
          ))}
        </div>

        {/* Top section — hidden on desktop (branding shown in split layout instead) */}
        <div className="ns-auth-splash" style={{textAlign:'center',padding:'60px 24px 20px',zIndex:1}}>
          {/* Logo */}
          <motion.div
            animate={{rotate:[0,5,-5,0],scale:[1,1.05,1]}}
            transition={{repeat:Infinity,duration:3}}>
            <div style={{width:'100px',height:'100px',borderRadius:'28px',
              background:'linear-gradient(135deg,#FFD600,#FF6B00)',
              display:'flex',alignItems:'center',justifyContent:'center',
              fontSize:'56px',margin:'0 auto 16px',
              boxShadow:'0 8px 32px rgba(255,214,0,0.5)'}}>
              ⭐
            </div>
          </motion.div>

          <motion.h1
            style={{fontSize:'42px',fontWeight:'900',color:'white',margin:'0 0 6px',
              letterSpacing:'-1px',textShadow:'0 0 40px rgba(255,214,0,0.5)'}}
            initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.2}}>
            NeevStar
          </motion.h1>
          <motion.p
            style={{fontSize:'16px',color:'rgba(255,255,255,0.7)',margin:'0 0 32px',fontWeight:'700'}}
            initial={{opacity:0}} animate={{opacity:1}} transition={{delay:0.4}}>
            Learn. Play. Grow. 🚀
          </motion.p>

          {/* Feature pills */}
          <motion.div style={{display:'flex',gap:'8px',flexWrap:'wrap',justifyContent:'center',marginBottom:'32px'}}
            initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.5}}>
            {['🏆 Olympiad Prep','🤖 AI Tutor','💰 Money School','🔬 STEM Lab','📰 Kids News'].map((f,i)=>(
              <span key={i} style={{background:'rgba(255,255,255,0.1)',borderRadius:'20px',
                padding:'6px 14px',fontSize:'12px',fontWeight:'800',color:'rgba(255,255,255,0.9)',
                border:'1px solid rgba(255,255,255,0.2)',backdropFilter:'blur(10px)'}}>
                {f}
              </span>
            ))}
          </motion.div>


          {/* Characters row */}
          <motion.div style={{display:'flex',justifyContent:'center',gap:'12px',marginBottom:'8px'}}
            initial={{opacity:0,scale:0.8}} animate={{opacity:1,scale:1}} transition={{delay:0.6}}>
            {['🦊','🐘','🦚','🐒'].map((emoji,i)=>(
              <motion.div key={i}
                style={{width:'60px',height:'60px',borderRadius:'50%',
                  background:`rgba(255,255,255,0.1)`,
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:'32px',border:'2px solid rgba(255,255,255,0.2)'}}
                animate={{y:[0,-8,0]}}
                transition={{duration:1.5,repeat:Infinity,delay:i*0.2}}>
                {emoji}
              </motion.div>
            ))}
          </motion.div>
          <p style={{fontSize:'12px',color:'rgba(255,255,255,0.5)',margin:0,fontWeight:'700'}}>
            Classes 3-8 • Learn • Play • Grow
          </p>
        </div>

        {/* Bottom CTA */}
        <div className="ns-auth-cta" style={{width:'100%',padding:'24px',zIndex:1,
          background:'linear-gradient(to top,rgba(0,0,0,0.5),transparent)'}}>

          {/* Google button */}
          <motion.button
            style={{width:'100%',padding:'16px',borderRadius:'16px',border:'none',
              background:'white',display:'flex',alignItems:'center',justifyContent:'center',
              gap:'12px',cursor:'pointer',marginBottom:'12px',
              fontFamily:"'Nunito',sans-serif",fontSize:'16px',fontWeight:'900',color:'#333',
              boxShadow:'0 4px 24px rgba(0,0,0,0.3)'}}
            whileTap={{scale:0.97}} whileHover={{scale:1.02}}
            initial={{opacity:0,y:30}} animate={{opacity:1,y:0}} transition={{delay:0.7}}
            onClick={handleGoogleLogin}>
            <svg width="22" height="22" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </motion.button>

          <div style={{display:'flex',gap:'10px'}}>
            <motion.button
              style={{flex:1,padding:'14px',borderRadius:'14px',border:'2px solid rgba(255,255,255,0.3)',
                background:'rgba(255,255,255,0.1)',cursor:'pointer',
                fontFamily:"'Nunito',sans-serif",fontSize:'15px',fontWeight:'800',color:'white',
                backdropFilter:'blur(10px)'}}
              whileTap={{scale:0.97}}
              initial={{opacity:0,y:30}} animate={{opacity:1,y:0}} transition={{delay:0.8}}
              onClick={()=>{setMode('login');setError('');}}>
              Log In
            </motion.button>
            <motion.button
              style={{flex:1,padding:'14px',borderRadius:'14px',border:'none',
                background:'linear-gradient(135deg,#FFD600,#FF6B00)',cursor:'pointer',
                fontFamily:"'Nunito',sans-serif",fontSize:'15px',fontWeight:'900',color:'white',
                boxShadow:'0 4px 16px rgba(255,107,0,0.4)'}}
              whileTap={{scale:0.97}}
              initial={{opacity:0,y:30}} animate={{opacity:1,y:0}} transition={{delay:0.9}}
              onClick={()=>{setMode('signup');setError('');setStep(1);}}>
              Sign Up Free 🚀
            </motion.button>
          </div>

          <p style={{textAlign:'center',fontSize:'11px',color:'rgba(255,255,255,0.4)',
            margin:'12px 0 0',fontWeight:'700'}}>
            600+ Questions • 5 Olympiad Subjects • Classes 3–8 • Free to start
          </p>
          {onTeacherPortal&&(
            <button onClick={onTeacherPortal}
              style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,
                margin:'16px auto 0',background:'rgba(255,255,255,0.15)',
                border:'2px solid rgba(255,255,255,0.4)',
                color:'white',borderRadius:'14px',padding:'11px 24px',cursor:'pointer',
                fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:13,width:'100%',
                maxWidth:320,boxSizing:'border-box'}}>
              🏫 Teacher / School Login
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── FORGOT PASSWORD SCREEN ──────────────────────────────────
  if(mode==='forgot'){
    return(
      <div style={{minHeight:'100vh',background:'linear-gradient(160deg,#0A0E27 0%,#1A0533 50%,#0D1B4B 100%)',
        display:'flex',flexDirection:'column',padding:'24px',fontFamily:"'Nunito',sans-serif"}}>
        <button onClick={()=>setMode('login')}
          style={{background:'none',border:'none',color:'rgba(255,255,255,0.7)',
            fontSize:'15px',fontWeight:'800',cursor:'pointer',textAlign:'left',
            marginBottom:'24px',fontFamily:"'Nunito',sans-serif"}}>
          ← Back to Login
        </button>
        <div style={{flex:1,display:'flex',flexDirection:'column',justifyContent:'center',maxWidth:'380px',width:'100%',margin:'0 auto'}}>
          <div style={{textAlign:'center',marginBottom:'28px'}}>
            <p style={{fontSize:'48px',margin:'0 0 8px'}}>📧</p>
            <h2 style={{fontSize:'24px',fontWeight:'900',color:'white',margin:'0 0 4px'}}>
              Forgot Password?
            </h2>
            <p style={{fontSize:'13px',color:'rgba(255,255,255,0.6)',fontWeight:'700'}}>
              Enter your email and we will send a reset link
            </p>
          </div>

          {error&&(
            <div style={{background:error.includes('sent')?'rgba(0,200,81,0.2)':'rgba(255,45,85,0.2)',
              borderRadius:'12px',padding:'12px 16px',marginBottom:'16px',fontSize:'13px',
              color:error.includes('sent')?'#00C851':'#FF6B8A',fontWeight:'700',
              border:error.includes('sent')?'1px solid rgba(0,200,81,0.3)':'1px solid rgba(255,45,85,0.3)'}}>
              {error}
            </div>
          )}

          <p style={{fontSize:'13px',color:'rgba(255,255,255,0.6)',margin:'0 0 8px',fontWeight:'700'}}>
            📧 Your Email
          </p>
          <input
            style={{...S.inputField,background:'rgba(255,255,255,0.1)',
              color:'white',borderColor:'rgba(255,255,255,0.2)',marginBottom:'24px'}}
            type="email" placeholder="Enter your email address"
            value={email} onChange={e=>setEmail(e.target.value)}/>

          <motion.button
            style={{width:'100%',padding:'16px',borderRadius:'16px',border:'none',
              background:'linear-gradient(135deg,#00695C,#1A1A6E)',color:'white',
              fontSize:'16px',fontWeight:'900',cursor:'pointer',
              fontFamily:"'Nunito',sans-serif",opacity:loading?0.7:1}}
            whileTap={{scale:0.97}}
            disabled={loading}
            onClick={async()=>{
              if(!email.trim()){setError('Please enter your email address');return;}
              setLoading(true);setError('');
              const{error:e}=await supabase.auth.resetPasswordForEmail(
                email.trim().toLowerCase(),
                {redirectTo:'https://neevstar.com'}
              );
              setLoading(false);
              if(e){
                setError('Could not send reset link. Please try again.');
              } else {
                setError('Reset link sent! Check your inbox AND spam folder. The link expires in 1 hour.');
              }
            }}>
            {loading?'Sending...':'Send Reset Link 📧'}
          </motion.button>

          <button onClick={()=>setMode('login')}
            style={{background:'none',border:'none',color:'rgba(255,255,255,0.5)',
              fontWeight:'700',cursor:'pointer',marginTop:'16px',fontSize:'13px',
              fontFamily:"'Nunito',sans-serif"}}>
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // ── LOGIN SCREEN ────────────────────────────────────────────
  if(mode==='login'){
    return(
      <div style={{minHeight:skipSplash?'unset':'100vh',
        background:skipSplash?'white':'linear-gradient(160deg,#0A0E27 0%,#1A0533 50%,#0D1B4B 100%)',
        display:'flex',flexDirection:'column',padding:'24px',fontFamily:"'Nunito',sans-serif"}}>
        {!skipSplash&&<button onClick={()=>setMode('landing')}
          style={{background:'none',border:'none',color:'rgba(255,255,255,0.7)',
            fontSize:'15px',fontWeight:'800',cursor:'pointer',textAlign:'left',
            marginBottom:'24px',fontFamily:"'Nunito',sans-serif"}}>
          ← Back
        </button>}
        <div style={{flex:1,display:'flex',flexDirection:'column',justifyContent:'center'}}>
          {!skipSplash&&<div style={{textAlign:'center',marginBottom:'28px'}}>
            <div style={{width:'72px',height:'72px',borderRadius:'20px',background:'linear-gradient(135deg,#FFD600,#FF6B00)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'38px',margin:'0 auto 10px',boxShadow:'0 6px 24px rgba(255,214,0,0.4)'}}>⭐</div>
            <p style={{fontSize:'12px',fontWeight:'900',color:'rgba(255,255,255,0.4)',margin:0,letterSpacing:'0.1em'}}>NEEVSTAR</p>
          </div>}
          <h2 style={{fontSize:'28px',fontWeight:'900',color:skipSplash?'#1A1A2E':'white',margin:'0 0 4px'}}>Welcome back! 👋</h2>
          <p style={{fontSize:'14px',color:skipSplash?'#888':'rgba(255,255,255,0.6)',margin:'0 0 24px',fontWeight:'700'}}>
            Login to continue your learning journey
          </p>

          {error&&<div style={{background:'rgba(255,45,85,0.1)',borderRadius:'12px',
            padding:'12px 16px',marginBottom:'16px',fontSize:'13px',
            color:'#FF2D55',fontWeight:'700',border:'1px solid rgba(255,45,85,0.2)'}}>
            {error}
          </div>}

          <p style={{fontSize:'13px',color:skipSplash?'#555':'rgba(255,255,255,0.6)',margin:'0 0 8px',fontWeight:'700'}}>📧 Email</p>
          <input style={{...S.inputField,
            background:skipSplash?'#F8F9FF':'rgba(255,255,255,0.12)',
            color:skipSplash?'#1A1A2E':'white',
            borderColor:skipSplash?'#E0E0E0':'rgba(255,255,255,0.3)',marginBottom:'16px'}}
            type="email" placeholder="your@email.com" value={email}
            onChange={e=>setEmail(e.target.value)}/>

          <p style={{fontSize:'13px',color:skipSplash?'#555':'rgba(255,255,255,0.6)',margin:'0 0 8px',fontWeight:'700'}}>🔑 Password</p>
          <div style={{position:'relative',marginBottom:'8px'}}>
            <input style={{...S.inputField,
              background:skipSplash?'#F8F9FF':'rgba(255,255,255,0.12)',
              color:skipSplash?'#1A1A2E':'white',
              borderColor:skipSplash?'#E0E0E0':'rgba(255,255,255,0.3)',marginBottom:0,width:'100%',boxSizing:'border-box'}}
              type={showPwd?'text':'password'} placeholder="Enter password" value={password}
              onChange={e=>setPassword(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&handleLogin()}/>
            <button onClick={()=>setShowPwd(v=>!v)} style={{position:'absolute',right:'14px',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',fontSize:'18px',padding:0,lineHeight:1}}>
              {showPwd?'🙈':'👁️'}
            </button>
          </div>

          <button onClick={()=>{setError('');setMode('forgot');}}
            style={{background:'none',border:'none',color:skipSplash?'#FF6B00':'rgba(255,255,255,0.5)',
            fontWeight:'700',cursor:'pointer',fontSize:'12px',textAlign:'right',
            marginBottom:'24px',fontFamily:"'Nunito',sans-serif"}}>
            Forgot password?
          </button>

          <motion.button style={{width:'100%',padding:'16px',borderRadius:'16px',border:'none',
            background:'linear-gradient(135deg,#00695C,#1A1A6E)',color:'white',
            fontSize:'16px',fontWeight:'900',cursor:'pointer',opacity:loading?0.7:1,
            fontFamily:"'Nunito',sans-serif",boxShadow:'0 4px 20px rgba(0,102,255,0.3)'}}
            whileTap={{scale:0.97}} onClick={handleLogin} disabled={loading}>
            {loading?'Logging in...':'Log In 🚀'}
          </motion.button>

          <button onClick={()=>setMode('signup')}
            style={{background:'none',border:'none',color:skipSplash?'#FF6B00':'rgba(255,255,255,0.6)',
              fontWeight:'700',cursor:'pointer',marginTop:'16px',fontSize:'14px',
              fontFamily:"'Nunito',sans-serif"}}>
            New to NeevStar? Sign up free →
          </button>
        </div>
      </div>
    );
  }

  // ── SIGNUP SCREEN ───────────────────────────────────────────
  return(
    <div style={{minHeight:skipSplash?'unset':'100vh',
      background:skipSplash?'white':'linear-gradient(160deg,#0A0E27 0%,#1A0533 50%,#0D1B4B 100%)',
      display:'flex',flexDirection:'column',padding:'24px',fontFamily:"'Nunito',sans-serif"}}>
      {!skipSplash&&<button onClick={()=>step===1?setMode('landing'):setStep(1)}
        style={{background:'none',border:'none',color:'rgba(255,255,255,0.7)',
          fontSize:'15px',fontWeight:'800',cursor:'pointer',textAlign:'left',
          marginBottom:'24px',fontFamily:"'Nunito',sans-serif"}}>
        ← Back
      </button>}

      <div style={{flex:1,display:'flex',flexDirection:'column',justifyContent:'center'}}>
        {error&&<div style={{background:'rgba(255,45,85,0.2)',borderRadius:'12px',
          padding:'12px 16px',marginBottom:'16px',fontSize:'13px',
          color:'#FF6B8A',fontWeight:'700',border:'1px solid rgba(255,45,85,0.3)'}}>
          {error}
        </div>}

        {step===1&&(
          <motion.div initial={{opacity:0,x:30}} animate={{opacity:1,x:0}}>
            {!skipSplash&&<div style={{textAlign:'center',marginBottom:'20px'}}>
              <div style={{width:'68px',height:'68px',borderRadius:'18px',background:'linear-gradient(135deg,#FFD600,#FF6B00)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'36px',margin:'0 auto 10px',boxShadow:'0 6px 20px rgba(255,214,0,0.4)'}}>⭐</div>
              <div style={{display:'flex',justifyContent:'center',gap:'6px'}}>
                {[1,2].map(s=><div key={s} style={{height:'4px',width:'32px',borderRadius:'2px',background:step>=s?'#FF6B00':'rgba(255,255,255,0.2)'}}/>)}
              </div>
            </div>}
            <h2 style={{fontSize:'28px',fontWeight:'900',color:skipSplash?'#1A1A2E':'white',margin:'0 0 4px'}}>
              Join NeevStar! 🌟
            </h2>
            <p style={{fontSize:'14px',color:skipSplash?'#888':'rgba(255,255,255,0.6)',margin:'0 0 16px',fontWeight:'700'}}>
              Step 1 of 2 — Create your account
            </p>
            {foundingCount!==null&&foundingCount<100&&<FoundingCounter count={foundingCount}/>}

            <p style={{fontSize:'13px',color:skipSplash?'#555':'rgba(255,255,255,0.6)',margin:'0 0 8px',fontWeight:'700'}}>😊 Your Name</p>
            <input style={{...S.inputField,background:skipSplash?'#F8F9FF':'rgba(255,255,255,0.12)',
              color:skipSplash?'#1A1A2E':'white',borderColor:skipSplash?'#E0E0E0':'rgba(255,255,255,0.3)'}}
              placeholder="What's your name?" value={name} onChange={e=>setName(e.target.value)}/>

            <p style={{fontSize:'13px',color:skipSplash?'#555':'rgba(255,255,255,0.6)',margin:'0 0 8px',fontWeight:'700'}}>📧 Parent's Email</p>
            <input style={{...S.inputField,background:skipSplash?'#F8F9FF':'rgba(255,255,255,0.12)',
              color:skipSplash?'#1A1A2E':'white',borderColor:skipSplash?'#E0E0E0':'rgba(255,255,255,0.3)'}}
              type="email" placeholder="parent@email.com" value={email} onChange={e=>setEmail(e.target.value)}/>

            <p style={{fontSize:'13px',color:skipSplash?'#555':'rgba(255,255,255,0.6)',margin:'0 0 8px',fontWeight:'700'}}>🔑 Create Password</p>
            <div style={{position:'relative',marginBottom:'24px'}}>
              <input style={{...S.inputField,background:skipSplash?'#F8F9FF':'rgba(255,255,255,0.12)',
                color:skipSplash?'#1A1A2E':'white',borderColor:skipSplash?'#E0E0E0':'rgba(255,255,255,0.3)',marginBottom:0,width:'100%',boxSizing:'border-box'}}
                type={showPwd?'text':'password'} placeholder="Min 6 characters" value={password} onChange={e=>setPassword(e.target.value)}/>
              <button onClick={()=>setShowPwd(v=>!v)} style={{position:'absolute',right:'14px',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',fontSize:'18px',padding:0,lineHeight:1}}>
                {showPwd?'🙈':'👁️'}
              </button>
            </div>

            <motion.button style={{width:'100%',padding:'16px',borderRadius:'16px',border:'none',
              background:'linear-gradient(135deg,#FFD600,#FF6B00)',color:'white',
              fontSize:'16px',fontWeight:'900',cursor:'pointer',
              fontFamily:"'Nunito',sans-serif",boxShadow:'0 4px 20px rgba(255,107,0,0.4)'}}
              whileTap={{scale:0.97}}
              onClick={()=>{
                if(name&&email&&password.length>=6){setError('');setStep(2);}
                else setError('Please fill all fields. Password must be 6+ characters.');
              }}>
              Continue →
            </motion.button>
          </motion.div>
        )}

        {step===2&&(
          <motion.div initial={{opacity:0,x:30}} animate={{opacity:1,x:0}}>
            <div style={{textAlign:'center',marginBottom:'20px'}}>
              <div style={{width:'68px',height:'68px',borderRadius:'18px',background:'linear-gradient(135deg,#FFD600,#FF6B00)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'36px',margin:'0 auto 10px',boxShadow:'0 6px 20px rgba(255,214,0,0.4)'}}>⭐</div>
              <div style={{display:'flex',justifyContent:'center',gap:'6px'}}>
                {[1,2].map(s=><div key={s} style={{height:'4px',width:'32px',borderRadius:'2px',background:step>=s?'#FF6B00':'rgba(255,255,255,0.2)'}}/>)}
              </div>
            </div>
            <h2 style={{fontSize:'28px',fontWeight:'900',color:skipSplash?'#1A1A2E':'white',margin:'0 0 4px'}}>
              Hi {name}! 🎉
            </h2>
            <p style={{fontSize:'14px',color:skipSplash?'#888':'rgba(255,255,255,0.6)',margin:'0 0 20px',fontWeight:'700'}}>
              Step 2 of 2 — Which class are you in?
            </p>

            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px',marginBottom:'24px'}}>
              {[3,4,5,6,7,8].map(c=>(
                <motion.button key={c} whileTap={{scale:0.92}}
                  style={{padding:'18px 0',borderRadius:'16px',border:'none',
                    background:cls===c?'linear-gradient(135deg,#FFD600,#FF6B00)':(skipSplash?'#F0F2FF':'rgba(255,255,255,0.12)'),
                    fontWeight:'900',fontSize:'22px',cursor:'pointer',
                    color:cls===c?'white':(skipSplash?'#FF6B00':'white'),
                    fontFamily:"'Nunito',sans-serif",
                    boxShadow:cls===c?'0 4px 16px rgba(255,107,0,0.5)':'none'}}
                  onClick={()=>setCls(c)}>{c}</motion.button>
              ))}
            </div>

            <motion.button
              style={{width:'100%',padding:'16px',borderRadius:'16px',border:'none',
                background:cls?'linear-gradient(135deg,#00C851,#00E676)':'rgba(255,255,255,0.1)',
                color:'white',fontSize:'16px',fontWeight:'900',cursor:'pointer',
                fontFamily:"'Nunito',sans-serif",
                boxShadow:cls?'0 4px 20px rgba(0,200,81,0.4)':'none',
                opacity:loading?0.7:1}}
              whileTap={{scale:0.97}} onClick={handleSignup} disabled={loading||!cls}>
              {loading?"Creating account...":"Let's Start Learning! 🚀"}
            </motion.button>
            <p style={{fontSize:'11px',color:'rgba(255,255,255,0.35)',textAlign:'center',margin:'16px 0 0',fontWeight:'700',lineHeight:'1.6'}}>
              By signing up, you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{color:'rgba(255,255,255,0.55)',textDecoration:'underline'}}>Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{color:'rgba(255,255,255,0.55)',textDecoration:'underline'}}>Privacy Policy</a>
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}


